"use client"

import { useMemo } from "react"
import { usePlayback } from "./usePlayback"
import { PlaybackControls } from "./PlaybackControls"
import type { ArrayStep } from "./types"

interface ExtraArray {
  label: string
  values: (number | string)[]
}

interface ArrayVizProps {
  initialArray: (number | string)[]
  steps: ArrayStep[]
  extraArrays?: ExtraArray[]
  width?: number
  height?: number
}

const CELL_SIZE = 50
const CELL_GAP = 8
const COMPARE_COLOR = "#f59e0b"
const SORTED_COLOR = "#22c55e"
const DEFAULT_HIGHLIGHT = "#3b82f6"
const DEFAULT_FILL = "#ffffff"

/**
 * Replays all steps from 0..upTo (inclusive) against the initial array,
 * returning the current array state, a highlight map, and which indices
 * were just swapped on the final step (for animation).
 */
function replaySteps(
  initialArray: (number | string)[],
  steps: ArrayStep[],
  upTo: number,
  extraCount: number
): {
  array: (number | string)[]
  highlights: Map<number, string>
  extraHighlights: Map<number, Map<number, string>>
  swappedPair: [number, number] | null
} {
  const array = [...initialArray]
  const highlights = new Map<number, string>()
  // Per-extra-array highlight maps: extraHighlights.get(arrayIndex) => Map<cellIndex, color>
  const extraHighlights = new Map<number, Map<number, string>>()
  for (let e = 0; e < extraCount; e++) {
    extraHighlights.set(e + 1, new Map())
  }
  let swappedPair: [number, number] | null = null

  for (let i = 0; i <= upTo; i++) {
    const step = steps[i]
    if (!step) continue

    if (i === upTo) {
      swappedPair = null
    }

    // Determine which array this step targets (0 = main, 1+ = extras)
    const target = step.arrayIndex ?? 0
    const targetHighlights = target === 0 ? highlights : (extraHighlights.get(target) ?? highlights)

    switch (step.type) {
      case "compare": {
        if (step.indices) {
          for (const idx of step.indices) {
            targetHighlights.set(idx, COMPARE_COLOR)
          }
        }
        break
      }

      case "swap": {
        if (target !== 0) break // swaps only on main array
        const a = step.fromIndex
        const b = step.toIndex
        if (a != null && b != null && a < array.length && b < array.length) {
          const tmp = array[a]!
          array[a] = array[b]!
          array[b] = tmp
          highlights.set(a, COMPARE_COLOR)
          highlights.set(b, COMPARE_COLOR)
          if (i === upTo) {
            swappedPair = [a, b]
          }
        }
        break
      }

      case "set": {
        if (step.indices && step.indices.length > 0 && step.value != null) {
          const idx = step.indices[0]!
          if (target === 0 && idx < array.length) {
            array[idx] = step.value!
          }
          targetHighlights.set(idx, step.color ?? DEFAULT_HIGHLIGHT)
        }
        break
      }

      case "highlight": {
        if (step.indices) {
          for (const idx of step.indices) {
            targetHighlights.set(idx, step.color ?? DEFAULT_HIGHLIGHT)
          }
        }
        break
      }

      case "unhighlight": {
        if (step.indices) {
          for (const idx of step.indices) {
            targetHighlights.delete(idx)
          }
        }
        break
      }

      case "shift": {
        if (step.range) {
          const [lo, hi] = step.range
          for (let r = lo; r <= hi; r++) {
            targetHighlights.set(r, step.color ?? DEFAULT_HIGHLIGHT)
          }
        }
        break
      }

      case "mark_sorted": {
        if (step.indices) {
          for (const idx of step.indices) {
            targetHighlights.set(idx, SORTED_COLOR)
          }
        }
        break
      }

      case "reset": {
        highlights.clear()
        for (const [, m] of extraHighlights) m.clear()
        break
      }
    }
  }

  return { array, highlights, extraHighlights, swappedPair }
}

export function ArrayViz({
  initialArray,
  steps,
  extraArrays = [],
  width = 600,
  height: heightProp,
}: ArrayVizProps) {
  const extraRowCount = extraArrays.length
  const ROW_HEIGHT = CELL_SIZE + 28 // cell + index labels + gap
  const height = heightProp ?? (200 + extraRowCount * ROW_HEIGHT)
  const {
    stepIndex,
    isPlaying,
    atStart,
    atEnd,
    stepForward,
    stepBack,
    play,
    pause,
    reset,
  } = usePlayback(steps.length)

  const { array, highlights, extraHighlights, swappedPair } = useMemo(
    () =>
      stepIndex < 0
        ? {
            array: [...initialArray],
            highlights: new Map<number, string>(),
            extraHighlights: new Map<number, Map<number, string>>(),
            swappedPair: null,
          }
        : replaySteps(initialArray, steps, stepIndex, extraArrays.length),
    [initialArray, steps, stepIndex, extraArrays.length]
  )

  const currentStep = stepIndex >= 0 ? steps[stepIndex] : undefined

  // Layout calculations
  const totalCellWidth = CELL_SIZE + CELL_GAP
  const maxLen = Math.max(array.length, ...extraArrays.map((e) => e.values.length))
  const totalWidth = maxLen * CELL_SIZE + (maxLen - 1) * CELL_GAP
  const offsetX = (width - totalWidth) / 2
  const extraHeight = extraRowCount * ROW_HEIGHT
  const offsetY = extraHeight + (height - extraHeight - CELL_SIZE) / 2 - 8

  return (
    <div className="flex flex-col gap-3">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="rounded-lg border border-border bg-background"
      >
        {/* Extra reference arrays (static, above the main array) */}
        {extraArrays.map((extra, rowIdx) => {
          const extraTotalWidth = extra.values.length * CELL_SIZE + (extra.values.length - 1) * CELL_GAP
          const extraOffsetX = (width - extraTotalWidth) / 2
          const rowY = 20 + rowIdx * ROW_HEIGHT
          return (
            <g key={`extra-${rowIdx}`} transform={`translate(${extraOffsetX}, ${rowY})`}>
              {/* Label */}
              <text
                x={-8}
                y={CELL_SIZE / 2}
                textAnchor="end"
                dominantBaseline="central"
                fontSize={11}
                fill="var(--color-muted-foreground)"
              >
                {extra.label}
              </text>
              {extra.values.map((val, i) => {
                const x = i * totalCellWidth
                const extraHL = extraHighlights.get(rowIdx + 1)
                const cellFill = extraHL?.get(i) ?? "var(--color-muted)"
                return (
                  <g key={i}>
                    <rect
                      x={x}
                      y={0}
                      width={CELL_SIZE}
                      height={CELL_SIZE}
                      rx={6}
                      fill={cellFill}
                      stroke="var(--color-border)"
                      strokeWidth={1}
                      style={{ transition: "fill 400ms ease" }}
                    />
                    <text
                      x={x + CELL_SIZE / 2}
                      y={CELL_SIZE / 2}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={14}
                      fontFamily="ui-monospace, monospace"
                      fill="var(--color-foreground)"
                    >
                      {val}
                    </text>
                    <text
                      x={x + CELL_SIZE / 2}
                      y={CELL_SIZE + 14}
                      textAnchor="middle"
                      fontSize={10}
                      fill="var(--color-muted-foreground)"
                    >
                      {i}
                    </text>
                  </g>
                )
              })}
            </g>
          )
        })}

        {/* Main array (animated) */}
        <g transform={`translate(${offsetX}, ${offsetY})`}>
          {array.map((value, i) => {
            const baseX = i * totalCellWidth
            const fill = highlights.get(i) ?? DEFAULT_FILL

            // Swap animation: if this cell was just swapped, translate from
            // the other cell's position to create a smooth motion.
            let translateX = 0
            if (swappedPair) {
              const [a, b] = swappedPair
              if (i === a) {
                translateX = (b - a) * totalCellWidth
              } else if (i === b) {
                translateX = (a - b) * totalCellWidth
              }
            }

            const isHighlighted = highlights.has(i)
            const textColor = isHighlighted ? "#ffffff" : "#1f2937"

            return (
              <g
                key={i}
                style={{
                  transition: "all 400ms ease",
                  transform: `translateX(${baseX}px)`,
                }}
              >
                {/* Cell background */}
                <rect
                  width={CELL_SIZE}
                  height={CELL_SIZE}
                  rx={6}
                  ry={6}
                  stroke="#d1d5db"
                  strokeWidth={1}
                  style={{
                    fill,
                    transition: "fill 400ms ease",
                  }}
                />

                {/* Value text */}
                <text
                  x={CELL_SIZE / 2}
                  y={CELL_SIZE / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={18}
                  fontWeight={600}
                  fontFamily="ui-monospace, monospace"
                  fill={textColor}
                  style={{ transition: "fill 400ms ease" }}
                >
                  {value}
                </text>

                {/* Index label */}
                <text
                  x={CELL_SIZE / 2}
                  y={CELL_SIZE + 16}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={11}
                  fill="#9ca3af"
                  fontFamily="ui-monospace, monospace"
                >
                  {i}
                </text>
              </g>
            )
          })}
        </g>
      </svg>

      <PlaybackControls
        message={currentStep?.message ?? ""}
        isPlaying={isPlaying}
        atStart={atStart}
        atEnd={atEnd}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        onStepBack={stepBack}
        onStepForward={stepForward}
        onPlay={play}
        onPause={pause}
        onReset={reset}
      />
    </div>
  )
}
